import _ from 'lodash';
import React from 'react';
import * as DataProviders from '../../types/data-providers';
import { useData, FetchMoreFunction } from './DataProviderCache';
import * as ApiRequests from '../../api/requests';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { Clock } from '../../lib/clock';
import { parseInteger } from '../../lib';
import { TimelineCardEvent } from '../../components/TimelineListItem/types';

type ContextState = {
    fetchMore: FetchMoreFunction;
    dataset: Array<
        DataProviders.Data<{
            paging: { offset: string | null; limit: string | null; dateTime: string | null; moreRecords: boolean };
            timeline: TimelineCardEvent[];
        }>
    >;
};

const Context = React.createContext<ContextState | null>(null);

function useTimelineData(): ContextState['dataset'] {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useTimelineData must be used within a TimelineDataProvider');
    }
    return contextState.dataset;
}

function useTimelineFetchMore(): ContextState['fetchMore'] {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useTimelineFetchMore must be used within a TimelineDataProvider');
    }
    return contextState.fetchMore;
}

const DEFAULT_LIMIT = 50;

const TimelineDataProvider: React.FC<{
    organizationIdOrLocationIdOrSiteId: string;
    limit?: number;
}> = (props) => {
    const authHelper = useAuthHelper();

    const dataObj = useData({
        cacheKey: 'site-alarm-timeline-data',
        id: props.organizationIdOrLocationIdOrSiteId,
        dataFn: React.useCallback(
            async (opts?: {
                paginationCursor?: {
                    dateTime: string;
                    offset: number;
                    limit: number;
                };
            }) => {
                const result = await ApiRequests.getAlarmTimeline(
                    {
                        organizationIdOrLocationIdOrSiteId: props.organizationIdOrLocationIdOrSiteId,
                        offset: (opts && opts.paginationCursor && opts.paginationCursor.offset) || 0,
                        limit: (opts && opts.paginationCursor && opts.paginationCursor.limit) || DEFAULT_LIMIT,
                        dateTime: (opts && opts.paginationCursor && opts.paginationCursor.dateTime) || undefined,
                    },
                    { authHelper }
                );

                if (result.ok) {
                    return {
                        paging: result.response.data.paging,
                        timeline: result.response.data.timeline,
                    };
                }

                throw new Error(ApiRequests.extractResponseNotOkMessage(result.response));
            },
            [authHelper, props.organizationIdOrLocationIdOrSiteId]
        ),
        opts: {
            pagination: {
                cursor: {
                    limit: props.limit || DEFAULT_LIMIT,
                    dateTime: Clock.now().toJSON(),
                    offset: 0,
                },
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                extractNextCursorFromResult: (result, prevCursor) => {
                    const offset = parseInteger(result.paging.offset) || 0;
                    const limit = parseInteger(result.paging.limit) || DEFAULT_LIMIT;
                    return {
                        limit: limit,
                        dateTime: result.paging.dateTime || prevCursor.dateTime,
                        offset: offset,
                    };
                },
            },
        },
    });

    return <Context.Provider value={dataObj}>{props.children}</Context.Provider>;
};

const TestTimelineDataProvider: React.FC<{ value: ContextState['dataset']; fetchMore?: FetchMoreFunction }> = (
    props
) => {
    const fetchMore = props.fetchMore || _.identity;
    return <Context.Provider value={{ dataset: props.value, fetchMore }}>{props.children}</Context.Provider>;
};

export { TimelineDataProvider, TestTimelineDataProvider, useTimelineData, useTimelineFetchMore };
